import { logger } from '../utils/logger';
import { applyBatch, invoke } from './dispatch';
import type { VelostructBridgeAPI } from './types';

function call(method: string, ...args: unknown[]): unknown {
  if (args.length <= 1) return invoke(method, args[0]);
  return invoke(method, args);
}

function wrapMethod<T extends (...args: never[]) => unknown>(method: string, fn: T): T {
  return ((...args: unknown[]) => {
    try {
      logger.debug('Bridge', `Calling ${method}`, args.length > 0 ? args[0] : undefined);
      const result = fn(...(args as never[]));
      logger.debug('Bridge', `${method} succeeded`, result);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error('Bridge', `Method "${method}" threw`, { args, error: msg }, err instanceof Error ? err : undefined);
      return { error: msg };
    }
  }) as unknown as T;
}

/**
 * Legacy window.__velostruct__ adapter.
 * Keeps positional signatures that MCP already uses, and forwards everything
 * through the object-params command kernel.
 */
export function createBridgeApi(): VelostructBridgeAPI {
  const api: VelostructBridgeAPI = {
    getSummary: () => call('getSummary') as object,
    getNodes: filter => call('getNodes', filter) as object[],
    findNode: labelOrId => call('findNode', labelOrId) as object | null,
    addNode: params => call('addNode', params) as ReturnType<VelostructBridgeAPI['addNode']>,
    updateNode: (labelOrId, updates) => call('updateNode', labelOrId, updates) as ReturnType<VelostructBridgeAPI['updateNode']>,
    removeNode: labelOrId => call('removeNode', labelOrId) as ReturnType<VelostructBridgeAPI['removeNode']>,
    connectNodes: params => call('connectNodes', params) as ReturnType<VelostructBridgeAPI['connectNodes']>,
    removeEdge: params => call('removeEdge', params) as ReturnType<VelostructBridgeAPI['removeEdge']>,
    updateEdge: (id, updates) => call('updateEdge', id, updates) as ReturnType<VelostructBridgeAPI['updateEdge']>,
    addParticipant: params => call('addParticipant', params) as ReturnType<VelostructBridgeAPI['addParticipant']>,
    removeParticipant: labelOrId => call('removeParticipant', labelOrId) as ReturnType<VelostructBridgeAPI['removeParticipant']>,
    getParticipants: () => call('getParticipants') as object[],
    addMessage: params => call('addMessage', params) as ReturnType<VelostructBridgeAPI['addMessage']>,
    updateMessage: (id, updates) => call('updateMessage', id, updates) as ReturnType<VelostructBridgeAPI['updateMessage']>,
    removeMessage: labelOrId => call('removeMessage', labelOrId) as ReturnType<VelostructBridgeAPI['removeMessage']>,
    getMessages: () => call('getMessages') as object[],
    addFragment: params => call('addFragment', params) as ReturnType<VelostructBridgeAPI['addFragment']>,
    removeFragment: id => call('removeFragment', id) as ReturnType<VelostructBridgeAPI['removeFragment']>,
    getFragments: () => call('getFragments') as object[],
    updateFragment: (id, updates) => call('updateFragment', id, updates) as ReturnType<VelostructBridgeAPI['updateFragment']>,
    insertMessage: params => call('insertMessage', params) as ReturnType<VelostructBridgeAPI['insertMessage']>,
    importFromMermaid: mmd => call('importFromMermaid', mmd) as ReturnType<VelostructBridgeAPI['importFromMermaid']>,
    exportToMermaid: () => call('exportToMermaid') as ReturnType<VelostructBridgeAPI['exportToMermaid']>,
    setPageDescription: (nameOrId, description) => call('setPageDescription', nameOrId, description) as ReturnType<VelostructBridgeAPI['setPageDescription']>,
    getPageDetail: nameOrId => call('getPageDetail', nameOrId) as ReturnType<VelostructBridgeAPI['getPageDetail']>,
    applyBatch: operations => applyBatch(operations),
    autoLayoutFlow: () => call('autoLayoutFlow') as ReturnType<VelostructBridgeAPI['autoLayoutFlow']>,
    reflowMessages: options => call('reflowMessages', options) as ReturnType<VelostructBridgeAPI['reflowMessages']>,
    recalcFragments: fragmentIds => call('recalcFragments', fragmentIds ? { fragmentIds } : {}) as ReturnType<VelostructBridgeAPI['recalcFragments']>,
    getEdges: () => call('getEdges') as object[],
    listPages: () => call('listPages') as object[],
    addPage: (name, diagramType) => call('addPage', name, diagramType) as ReturnType<VelostructBridgeAPI['addPage']>,
    switchPage: nameOrId => call('switchPage', nameOrId) as ReturnType<VelostructBridgeAPI['switchPage']>,
    linkNodeToPage: (nodeLabelOrId, pageNameOrId) => call('linkNodeToPage', nodeLabelOrId, pageNameOrId) as ReturnType<VelostructBridgeAPI['linkNodeToPage']>,
    frameNodes: (labelOrIds, label) => call('frameNodes', labelOrIds, label) as ReturnType<VelostructBridgeAPI['frameNodes']>,
    createGroup: (labelOrIds, label) => call('createGroup', labelOrIds, label) as ReturnType<VelostructBridgeAPI['createGroup']>,
    dissolveGroup: labelOrId => call('dissolveGroup', labelOrId) as ReturnType<VelostructBridgeAPI['dissolveGroup']>,
    clearDiagram: diagramType => call('clearDiagram', diagramType) as ReturnType<VelostructBridgeAPI['clearDiagram']>,
    setDiagramType: type => call('setDiagramType', type) as ReturnType<VelostructBridgeAPI['setDiagramType']>,
    getDiagramJson: () => call('getDiagramJson') as string,
    setDiagramJson: json => call('setDiagramJson', json) as ReturnType<VelostructBridgeAPI['setDiagramJson']>,
    getAIContext: options => call('getAIContext', options) as object,
    exportToPng: options => call('exportToPng', options) as ReturnType<VelostructBridgeAPI['exportToPng']>,
    updateNodeMeta: (labelOrId, updates) => call('updateNodeMeta', labelOrId, updates) as ReturnType<VelostructBridgeAPI['updateNodeMeta']>,
    getCatalog: () => call('getCatalog') as object[],
    updateCatalog: (catalogId, patch) => call('updateCatalog', catalogId, patch) as ReturnType<VelostructBridgeAPI['updateCatalog']>,
    placeCatalogInstance: (catalogId, position) => call('placeCatalogInstance', catalogId, position) as ReturnType<VelostructBridgeAPI['placeCatalogInstance']>,
    createChildView: (nodeLabelOrId, options) => call('createChildView', nodeLabelOrId, options) as ReturnType<VelostructBridgeAPI['createChildView']>,
    createC4Workspace: systemName => call('createC4Workspace', systemName) as ReturnType<VelostructBridgeAPI['createC4Workspace']>,
  };

  const safeApi = {} as VelostructBridgeAPI;
  (Object.keys(api) as (keyof VelostructBridgeAPI)[]).forEach(key => {
    safeApi[key] = wrapMethod(String(key), api[key] as (...args: never[]) => unknown) as never;
  });
  return safeApi;
}
